import React, { useEffect, useState } from 'react';
import { showToast } from '@/src/globalFunction';
import {
  useDeleteProjectTag,
  useDetachInboxDocumentTag,
  useGetProjectTags,
  useUpdateProjectTag,
  invalidateAllFilesQueries,
} from './api/projectTags';
import AssignedTags from './Elements/AssignedTags';
import ConfirmTagActionModal from './Elements/ConfirmTagActionModal';
import TagEntry from './Tag';
import { TagType } from './types';
import { useHasFullTagsAccess } from './utils';
import { useTagsStore, useTagsStoreContext } from '../Inbox2/store/tags.store';

interface FilesTableTagsManagerProps {
  assignedTags: any;
  setAssignedTags: any;
  projectId: any;
  rowId: any;
  inboxDocumentId: any;
  openTagModal: { rowId: string | null; tagIndex: number | null };
  setOpenTagModal: (modal: { rowId: string | null; tagIndex: number | null }) => void;
}

const FilesTableTagsManager = ({
  assignedTags,
  setAssignedTags,
  projectId,
  rowId,
  inboxDocumentId,
  openTagModal,
  setOpenTagModal,
}: FilesTableTagsManagerProps) => {
  const [editState, setEditState] = useState<{
    openId: string | null;
    editTagIndex: number | null;
  }>({ openId: null, editTagIndex: null });
  const { updateRowTags, setTags } = useTagsStore((state) => state);
  const { permissions } = useTagsStoreContext();
  const hasFullTagsAccess = useHasFullTagsAccess(permissions.inbox.actions);
  const { data: projectTags, refetch: refetchProjectTags } = useGetProjectTags(projectId, {
    enabled: hasFullTagsAccess,
  });
  const allProjectTags = projectTags || [];
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState<null | number>(null);
  const [pendingUpdate, setPendingUpdate] = useState<{
    tagIndex: number;
    updatedTag: TagType;
  } | null>(null);
  const updateProjectTagMutation = useUpdateProjectTag(projectId);
  const deleteProjectTagMutation = useDeleteProjectTag(Number(projectId));
  const detachInboxDocumentTagMutation = useDetachInboxDocumentTag(inboxDocumentId);

  // Only show the modal if openTagModal matches this row and tag index
  const isModalOpen =
    openTagModal &&
    openTagModal.rowId === rowId &&
    openTagModal.tagIndex !== null &&
    openTagModal.tagIndex >= 0 &&
    openTagModal.tagIndex < assignedTags.length;

  const handleEditTag = (tagIndex: number) =>
    setEditState({ openId: rowId, editTagIndex: tagIndex });
  const handleEditTagSave = (tagIndex: number, updatedTag: TagType) => {
    setPendingUpdate({ tagIndex, updatedTag });
    setShowUpdateConfirmModal(tagIndex);
  };
  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      handleRequestUpdate(pendingUpdate.tagIndex, pendingUpdate.updatedTag);
    }
    setShowUpdateConfirmModal(null);
    setPendingUpdate(null);
  };
  const handleRequestUpdate = (tagIndex: number, updatedTag: TagType) => {
    if (!projectId) return;
    updateProjectTagMutation.mutate(
      {
        tagId: updatedTag.id,
        body: { name: updatedTag.name, color: updatedTag.color, version: updatedTag.version },
      },
      {
        onSuccess: (updatedTagFromServer) => {
          const newTags = assignedTags.map((tag, idx) =>
            idx === tagIndex ? { ...tag, ...updatedTagFromServer } : tag
          );
          updateRowTags(rowId, newTags);
          setTags(newTags);
          showToast('success', 'Tag updated successfully');
          if (setAssignedTags) setAssignedTags(newTags);
          setEditState({ openId: null, editTagIndex: null });
        },
      }
    );
  };

  const handleRemoveTag = (tagIndex: number) => {
    setOpenTagModal({ rowId, tagIndex });
  };

  const handleConfirmDelete = () => {
    if (!openTagModal || openTagModal.rowId !== rowId || openTagModal.tagIndex === null) return;
    const tag = assignedTags[openTagModal.tagIndex];
    if (!tag) return;
    setOpenTagModal({ rowId: null, tagIndex: null });
    deleteProjectTagMutation.mutate(
      { tagId: Number(tag.id), version: tag.version },
      {
        onSuccess: () => {
          const newTags = assignedTags.filter((_, idx) => idx !== openTagModal.tagIndex);
          updateRowTags(rowId, newTags);
          setTags(newTags);
          showToast('success', 'Tag deleted successfully');
          invalidateAllFilesQueries();
          refetchProjectTags();
          setEditState({ openId: null, editTagIndex: null });
        },
      }
    );
  };

  const handleUnassignTag = (tagIndex: number) => {
    const tag = assignedTags[tagIndex];
    if (!tag || !inboxDocumentId) return;
    detachInboxDocumentTagMutation.mutate(tag.id, {
      onSuccess: () => {
        const newTags = assignedTags.filter((_, idx) => idx !== tagIndex);
        updateRowTags(rowId, newTags);
        if (setAssignedTags) setAssignedTags(newTags);
      },
    });
  };

  useEffect(() => {
    if (rowId && assignedTags) {
      updateRowTags(rowId, assignedTags);
    }
  }, [rowId, assignedTags, updateRowTags]);

  useEffect(() => {
    if (Array.isArray(projectTags)) {
      setTags(projectTags);
    }
  }, [projectTags, setTags]);

  return (
    <>
      {assignedTags.length > 0 ? (
        <>
          <AssignedTags
            assignedTags={assignedTags}
            rowId={rowId}
            onEdit={handleEditTag}
            isFilesTable={true}
            onRemoveTag={handleRemoveTag}
            onUnassignTag={handleUnassignTag}
            isTagReadOnly={!hasFullTagsAccess}
          />
          {editState.openId === rowId && editState.editTagIndex !== null && (
            <TagEntry
              id={rowId}
              openId={editState.openId}
              setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
              assignedTags={assignedTags}
              setAssignedTags={setAssignedTags}
              editTagIndex={editState.editTagIndex}
              setEditTagIndex={(idx) => setEditState((prev) => ({ ...prev, editTagIndex: idx }))}
              allProjectTags={allProjectTags}
              inboxDocumentId={inboxDocumentId}
              onRequestUpdate={() => {}}
              isFilesTable={true}
              onSave={handleEditTagSave}
              isTagReadOnly={!hasFullTagsAccess}
            />
          )}
        </>
      ) : (
        <TagEntry
          id={rowId}
          openId={editState.openId}
          setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
          assignedTags={assignedTags}
          setAssignedTags={setAssignedTags}
          allProjectTags={allProjectTags}
          inboxDocumentId={inboxDocumentId}
          onRequestUpdate={handleRequestUpdate}
          isFilesTable={true}
          isTagReadOnly={!hasFullTagsAccess}
        />
      )}
      {isModalOpen && (
        <ConfirmTagActionModal
          onClose={() => setOpenTagModal({ rowId: null, tagIndex: null })}
          onSubmit={handleConfirmDelete}
          subject={'Tag'}
          actionType="delete"
        />
      )}
      {showUpdateConfirmModal !== null && (
        <ConfirmTagActionModal
          onClose={() => {
            setShowUpdateConfirmModal(null);
            setPendingUpdate(null);
          }}
          onSubmit={handleConfirmUpdate}
          subject={'Tag'}
          actionType="update"
        />
      )}
    </>
  );
};

export default FilesTableTagsManager;
